import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  User, Mail, Shield, Brain, FileText, Zap, Clock,
  Building2, Crown, ChevronRight, Activity, Calendar,
  Lock, Eye, EyeOff, CheckCircle2, Loader2
} from "lucide-react";
import PrivacySection from "@/components/PrivacySection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const InfoRow = ({ icon: Icon, label, value, color = "purple" }) => (
  <div className="flex items-center gap-4 py-3" data-testid={`profile-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className={`w-9 h-9 rounded-xl bg-${color}-500/10 flex items-center justify-center flex-shrink-0`}>
      <Icon className={`w-4 h-4 text-${color}-400`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-white font-medium truncate">{value || "--"}</p>
    </div>
  </div>
);

const StatBox = ({ icon: Icon, label, value, sub, color = "purple" }) => (
  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-testid={`profile-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 text-${color}-400`} />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white font-mono">{value}</p>
    {sub && <p className="text-[10px] text-gray-600 mt-1">{sub}</p>}
  </div>
);

const SetPasswordSection = ({ hasPassword }) => {
  const [expanded, setExpanded] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (newPassword.length < 8) { toast.error("La contrasena debe tener al menos 8 caracteres"); return; }
    if (newPassword !== confirmPassword) { toast.error("Las contrasenas no coinciden"); return; }
    setLoading(true);
    try {
      const payload = { new_password: newPassword };
      if (hasPassword) payload.current_password = currentPassword;
      const res = await axios.post(`${API}/auth/set-password`, payload, {});
      toast.success(res.data.message);
      setSuccess(true);
      setExpanded(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al establecer contrasena");
    }
    setLoading(false);
  };

  return (
    <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6 mb-6" data-testid="set-password-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-500/10 flex items-center justify-center">
            <Lock className="w-4 h-4 text-orange-400" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">{hasPassword && !success ? "Cambiar Contrasena" : success ? "Contrasena Configurada" : "Establecer Contrasena"}</h2>
            <p className="text-xs text-gray-500">{hasPassword ? "Actualiza tu contrasena de acceso" : "Agrega una contrasena para iniciar sesion con email"}</p>
          </div>
        </div>
        {success ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
        ) : (
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-purple-400 hover:text-purple-300 transition-colors" data-testid="set-password-toggle">
            {expanded ? "Cancelar" : hasPassword ? "Cambiar" : "Configurar"}
          </button>
        )}
      </div>
      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="mt-4 space-y-3">
          {hasPassword && (
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Contrasena actual</label>
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-purple-500/50"
                placeholder="Tu contrasena actual" data-testid="current-password-input" />
            </div>
          )}
          <div className="relative">
            <label className="text-xs text-gray-400 mb-1 block">Nueva contrasena (min. 8 caracteres)</label>
            <input type={showNew ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-purple-500/50 pr-10"
              placeholder="Nueva contrasena" data-testid="new-password-input" />
            <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-7 text-gray-500 hover:text-white">
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Confirmar contrasena</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-purple-500/50"
              placeholder="Repite la contrasena" data-testid="confirm-password-input" />
          </div>
          {newPassword && confirmPassword && newPassword !== confirmPassword && (
            <p className="text-xs text-red-400">Las contrasenas no coinciden</p>
          )}
          <button onClick={handleSubmit} disabled={loading || newPassword.length < 8 || newPassword !== confirmPassword}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold disabled:opacity-30 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            data-testid="set-password-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {hasPassword ? "Actualizar Contrasena" : "Establecer Contrasena"}
          </button>
        </motion.div>
      )}
    </div>
  );
};

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadProfile = useCallback(async () => {
    try {
      const headers = { "Content-Type": "application/json" };
      const raw = localStorage.getItem("proxima_session_token");
      if (raw) {
        headers.Authorization = `Bearer ${decodeURIComponent(escape(atob(raw)))}`;
      }
      const resp = await fetch(`${API}/user/profile`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setProfile(data);
      }
    } catch (e) { console.error("Profile load failed:", e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <motion.div className="w-12 h-12 rounded-full border-4 border-purple-500/30 border-t-purple-500" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <p className="text-gray-400">Error al cargar perfil</p>
      </div>
    );
  }

  const planLabel = profile.plan === "free" ? "Free" : profile.plan === "pro" || profile.plan === "free_access" ? "Pro" : profile.plan || "Free";
  const isPro = planLabel === "Pro";

  return (
    <div className="min-h-screen bg-[#030014] pt-28 pb-16 px-4 sm:px-6 lg:px-8" data-testid="user-profile-page">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="text-center mb-8">
            <div className="relative inline-block mb-4">
              {profile.picture ? (
                <img src={profile.picture} alt={profile.name} className="w-24 h-24 rounded-full border-4 border-purple-500/30 object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/30 border-4 border-purple-500/30 flex items-center justify-center">
                  <User className="w-10 h-10 text-purple-400" />
                </div>
              )}
              <div className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center border-2 border-[#030014] ${isPro ? "bg-gradient-to-r from-amber-500 to-orange-500" : "bg-gray-700"}`}>
                <Crown className={`w-4 h-4 ${isPro ? "text-white" : "text-gray-400"}`} />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight">{profile.name}</h1>
            <p className="text-gray-400 mt-1">{profile.email}</p>
            <span className={`inline-block mt-3 px-4 py-1 rounded-full text-xs font-bold ${isPro ? "bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/30" : "bg-white/5 text-gray-400 border border-white/10"}`}>
              Plan {planLabel}
            </span>
          </div>

          {/* Info Card */}
          <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6 mb-6">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Informacion Personal</h2>
            <div className="divide-y divide-white/[0.05]">
              <InfoRow icon={User} label="Nombre" value={profile.name} />
              <InfoRow icon={Mail} label="Email" value={profile.email} color="cyan" />
              <InfoRow icon={Shield} label="Plan" value={planLabel} color={isPro ? "amber" : "gray"} />
              <InfoRow icon={Calendar} label="Miembro desde" value={profile.created_at ? new Date(profile.created_at).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" }) : "--"} color="emerald" />
              {profile.brand && <InfoRow icon={Building2} label="Empresa" value={profile.brand.company_name} color="pink" />}
              {profile.is_admin && <InfoRow icon={Shield} label="Rol" value="Administrador" color="red" />}
            </div>
          </div>

          {/* Password Section */}
          <SetPasswordSection hasPassword={profile.has_password} />

          {/* Privacy & Data Rights (GDPR) */}
          <PrivacySection />

          {/* Usage Stats */}
          <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6 mb-6">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Uso de la Plataforma</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatBox icon={Brain} label="Generaciones IA" value={profile.stats.ai_generations} color="purple" />
              <StatBox icon={FileText} label="Documentos" value={profile.stats.documents_created} color="cyan" />
              <StatBox icon={Zap} label="Crisis Sims" value={profile.stats.crisis_simulations} color="red" />
              <StatBox icon={Activity} label="Briefs" value={profile.stats.briefs_generated} color="emerald" />
              <StatBox icon={Activity} label="Tokens usados" value={`${(profile.stats.total_tokens / 1000).toFixed(0)}K`} color="pink" />
              <StatBox icon={Activity} label="Costo IA" value={`$${profile.stats.total_cost_usd.toFixed(2)}`} sub="estimado" color="amber" />
            </div>
          </div>

          {/* Recent Sessions */}
          {profile.recent_sessions?.length > 0 && (
            <div className="rounded-2xl bg-[#0A0A0F] border border-white/[0.08] p-6 mb-6">
              <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Sesiones Recientes</h2>
              <div className="space-y-2">
                {profile.recent_sessions.map((s, i) => (
                  <div key={`session-${i}`} className="flex items-center gap-3 py-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-300">{s.created_at ? new Date(s.created_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : "--"}</span>
                    {i === 0 && <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/20 text-emerald-400">Actual</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick links */}
          <div className="space-y-2">
            <button onClick={() => navigate("/brand-profile")} className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-purple-500/20 transition-all text-left" data-testid="profile-link-brand">
              <Building2 className="w-5 h-5 text-purple-400" />
              <div className="flex-1"><p className="text-sm text-white font-medium">Configuracion de Marca</p><p className="text-xs text-gray-500">Personaliza como PROXIMA comunica por tu marca</p></div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
            {profile.is_admin && (
              <button onClick={() => navigate("/admin")} className="w-full flex items-center gap-4 p-4 rounded-xl bg-red-500/5 border border-red-500/10 hover:bg-red-500/10 transition-all text-left" data-testid="profile-link-admin">
                <Shield className="w-5 h-5 text-red-400" />
                <div className="flex-1"><p className="text-sm text-white font-medium">Admin Command Center</p><p className="text-xs text-gray-500">Panel de control ejecutivo</p></div>
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            )}
            <button onClick={() => navigate("/pricing")} className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-amber-500/20 transition-all text-left" data-testid="profile-link-pricing">
              <Crown className="w-5 h-5 text-amber-400" />
              <div className="flex-1"><p className="text-sm text-white font-medium">{isPro ? "Gestionar Plan Pro" : "Upgrade a Pro"}</p><p className="text-xs text-gray-500">{isPro ? "Ver detalles de tu plan" : "Desbloquea todas las herramientas"}</p></div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UserProfile;
